import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useWorkspace } from './WorkspaceContext';
import * as fontService from '../services/fontService';
import { injectFontCss } from '@jigflow/shared/utils/fontLoader';
import type { Font, FieldFont } from '@jigflow/shared';

interface FontContextValue {
  fonts: Font[];
  loading: boolean;
  error: string | null;
  addFont(file: File, name: string, family: string): Promise<Font>;
  removeFont(id: string): Promise<void>;
  getFieldFonts(fieldId: string): Promise<FieldFont[]>;
  addFieldFont(fieldId: string, fontId: string, isPrimary?: boolean): Promise<void>;
  removeFieldFont(fieldId: string, fontId: string): Promise<void>;
  setFieldPrimaryFont(fieldId: string, fontId: string): Promise<void>;
  refreshFonts(): Promise<void>;
}

const FontContext = createContext<FontContextValue | null>(null);

export function FontProvider({ children }: { children: React.ReactNode }) {
  const { workspace } = useWorkspace();
  const [fonts, setFonts] = useState<Font[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const workspaceId = workspace?.id;

  const fetchFonts = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fontService.getFonts(workspaceId);
      setFonts(data);
    } catch (err) {
      console.error('[Font] Failed to fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load fonts');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchFonts();
  }, [fetchFonts]);

  // Inject @font-face CSS whenever fonts change so SvgPreview can render them
  useEffect(() => {
    if (fonts.length > 0) {
      injectFontCss(fonts);
    }
  }, [fonts]);

  const addFont = useCallback(
    async (file: File, name: string, family: string) => {
      if (!workspaceId) throw new Error('No workspace');
      const created = await fontService.uploadFont(workspaceId, file, name, family);
      setFonts((prev) => [...prev, created]);
      return created;
    },
    [workspaceId]
  );

  const removeFont = useCallback(
    async (id: string) => {
      await fontService.deleteFont(id);
      setFonts((prev) => prev.filter((f) => f.id !== id));
    },
    []
  );

  const getFieldFonts = useCallback(
    (fieldId: string) => fontService.getFieldFonts(fieldId),
    []
  );

  const addFieldFont = useCallback(
    (fieldId: string, fontId: string, isPrimary?: boolean) => {
      if (!workspaceId) throw new Error('No workspace');
      return fontService.addFieldFont(workspaceId, fieldId, fontId, isPrimary);
    },
    [workspaceId]
  );

  const removeFieldFont = useCallback(
    (fieldId: string, fontId: string) => fontService.removeFieldFont(fieldId, fontId),
    []
  );

  const setFieldPrimaryFont = useCallback(
    (fieldId: string, fontId: string) => fontService.setFieldPrimaryFont(fieldId, fontId),
    []
  );

  return (
    <FontContext.Provider
      value={{
        fonts,
        loading,
        error,
        addFont,
        removeFont,
        getFieldFonts,
        addFieldFont,
        removeFieldFont,
        setFieldPrimaryFont,
        refreshFonts: fetchFonts,
      }}
    >
      {children}
    </FontContext.Provider>
  );
}

export function useFonts() {
  const context = useContext(FontContext);
  if (!context) {
    throw new Error('useFonts must be used within a FontProvider');
  }
  return context;
}
